import { Component, input, output } from '@angular/core';
import { ProductKeys } from '@config/keys/product.keys';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { hasDiscountPromotionLabel } from '@core/services/eligible-promotions-labels/has-discount-promotion-label.util';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { CounterAtomComponent } from '@shared/atoms/counter-atom/counter-atom.component';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
import { RadialGradientDirective } from '@shared/directives/radial-gradient.directive';

@Component({
  selector: 'app-kiosk-product-card-atom',
  standalone: true,
  templateUrl: './kiosk-product-card-atom.component.html',
  styleUrls: ['./kiosk-product-card-atom.component.scss'],
  imports: [
    ContentBuilderColorPipe,
    ButtonAtomComponent,
    CounterAtomComponent,
    TranslatePipe,
    PriceAtomComponent,
    RadialGradientDirective,
  ],
  host: {
    '(click)': 'onCardClicked($event)',
  },
  styles: `
    :host {
      /* Custom elements are inline. A border on an inline host that wraps a
         block child paints a stray fragment above the card. */
      @apply block h-full;
    }
    :host.borderedCard {
      @apply rounded border border-solid border-neutral-300 bg-neutral-50 p-1;
      > div {
        @apply rounded p-1;
      }
    }

    :host.shadowCard {
      box-shadow: var(--kiosk-card-shadow);
      @apply p-2;
    }
    :host.roundedCard {
      @apply !rounded-2xl;
      > div {
        @apply !rounded-2xl;
      }
      .kiosk-card-image,
      .kiosk-card-image > * {
        @apply !rounded-2xl;
      }
    }
  `,
})
export class KioskProductCardAtomComponent {
  readonly product = input.required<DoodProductModel>();
  readonly quantity = input.required<number | undefined>();
  readonly isProductAvailable = input<boolean | undefined>(true);

  readonly isOrderingAllowed = input(true);
  readonly textColor = input<IContentBuilderFieldColor>();
  readonly cardColor = input<IContentBuilderFieldColor>();
  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  readonly buttonTextColor = input<IContentBuilderFieldColor>();
  readonly priceColor = input<IContentBuilderFieldColor>();
  readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  readonly secondaryColor = input<IContentBuilderFieldColor>();
  readonly displayPrice = input<boolean | undefined>(true);
  readonly displayedQuantity = input(0);
  readonly selectionnable = input<boolean | undefined>(false);
  readonly maxProduct = input(1);
  readonly monoSelection = input(false);
  readonly displayedAddition = input<number | undefined>();
  readonly isShowProductIcon = input<boolean>(true);

  readonly buttonClick = output<DoodProductModel>();
  readonly addProduct = output<void>();
  readonly showProduct = output<void>();
  readonly deleteProductProduct = output<DoodProductModel>();
  readonly changeProductQuantity = output<number>();

  // Track image loading state for fade-in effect
  public imageLoaded = false;

  public get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  public get effectiveIsProductAvailable(): boolean {
    const isStockUnavailable =
      this.product()?.[ProductKeys.InventoryTracked] === true &&
      (this.product()?.[ProductKeys.InventoryAvailable] ?? 0) <= 0;

    return (this.isProductAvailable() ?? true) && !isStockUnavailable;
  }

  public get canSelect(): boolean {
    return !!this.selectionnable() && this.effectiveIsProductAvailable;
  }

  goToProduct(product: DoodProductModel): void {
    if (!this.effectiveIsProductAvailable) {
      return;
    }
    this.buttonClick.emit(product);
  }

  displayDiscount(product: DoodProductModel): boolean {
    const discount = product[ProductKeys.Discount];
    return (discount && discount > 0) === true;
  }

  get _hasDiscountPromotionLabel(): boolean {
    return hasDiscountPromotionLabel(this.product());
  }

  addToCart(): void {
    this.addProduct.emit();
  }

  showProductModal(): void {
    this.showProduct.emit();
  }

  changeNbProduct(quantity: number) {
    this.changeProductQuantity.emit(quantity);
  }

  // Handle image load event to trigger fade-in effect
  onImageLoad(): void {
    this.imageLoaded = true;
  }

  onCardClicked(event: Event): void {
    const target = event.target as HTMLElement;

    if (!this.canSelect || target.closest('app-counter-atom')) {
      return;
    }

    switch (this.quantity()) {
      case 0:
      case undefined:
      case null:
        this.changeNbProduct(1);
        break;
      case 1:
        this.changeNbProduct(0);
        break;
      default:
        break;
    }
  }
}
